import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/Breadcrumb";
import EvidenceCard from "@/components/dashboard/EvidenceCard";
import { SHOW_MATCH_PERCENTAGE } from "@/config/displayPolicy";
import { useAnalysisDetail, useAssessments } from "@/hooks/useAnalysis";
import type { AnalysisResultItem } from "@/types";
import {
  ASSESSMENT_STATUS_LABEL,
  assessmentBadgeClass,
} from "@/utils/assessment";

const analysisTypeLabels: Record<string, string> = {
  pattern_matching: "패턴 매칭",
  compound_inference: "화합물 추론",
  hazard_assessment: "위험성 평가",
  full_analysis: "종합 분석",
};

const statusLabels: Record<string, string> = {
  pending: "대기",
  running: "실행 중",
  completed: "완료",
  failed: "실패",
};

const statusColor = (status: string) => {
  switch (status) {
    case "pending":
      return "text-gray-700 bg-gray-100";
    case "running":
      return "text-yellow-700 bg-yellow-100";
    case "completed":
      return "text-green-700 bg-green-100";
    case "failed":
      return "text-red-700 bg-red-100";
    default:
      return "text-gray-700 bg-gray-100";
  }
};

function HazardIndicator({
  value,
  label,
}: {
  value: number | null;
  label: string;
}) {
  if (value === null || value === undefined) return null;
  const isActive = value > 0;
  return (
    <span
      className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full mr-1 ${
        isActive ? "text-red-700 bg-red-100" : "text-gray-500 bg-gray-100"
      }`}
    >
      {label}
    </span>
  );
}

export default function AnalysisDetailPage() {
  const { id } = useParams();
  const {
    data: detail,
    isLoading,
    error,
  } = useAnalysisDetail(id ? Number(id) : undefined);

  // T2-3: 후보별 현재 판정 (result_id → 최신 판정)
  const { data: assessmentData } = useAssessments(id ? Number(id) : undefined);
  const assessmentByResult = new Map(
    (assessmentData?.current ?? []).map((a) => [a.result_id, a]),
  );

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">분석 상세</h1>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-1/3 mb-4" />
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-3" />
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-3" />
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-3" />
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-3" />
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-3" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">분석 상세</h1>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-600 font-medium">
            데이터를 불러오는 중 오류가 발생했습니다.
          </p>
          <p className="text-red-500 text-sm mt-1">
            {(error as Error).message}
          </p>
        </div>
        <div className="mt-4">
          <Link
            to="/analyses"
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            뒤로
          </Link>
        </div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">분석 상세</h1>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
          <p className="text-gray-500">분석 데이터를 찾을 수 없습니다.</p>
        </div>
        <div className="mt-4">
          <Link
            to="/analyses"
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            뒤로
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <Breadcrumb
        items={[
          { label: "대시보드", to: "/" },
          { label: "분석", to: "/analyses" },
          { label: `분석 #${detail.id}` },
        ]}
      />

      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">분석 상세</h1>
          <p className="text-gray-600">
            분석 ID: {detail.id}의 상세 정보를 확인합니다.
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Link
            to={`/analyses/${detail.id}/dashboard`}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            분석 대시보드
          </Link>
        </div>
      </div>

      {/* 기본 정보 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">기본 정보</h2>
        </div>
        <div className="p-6">
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">분석 ID</dt>
              <dd className="mt-1 text-sm text-gray-900">{detail.id}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">분석 유형</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {analysisTypeLabels[detail.analysis_type] ??
                  detail.analysis_type}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">상태</dt>
              <dd className="mt-1">
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColor(detail.status)}`}
                >
                  {statusLabels[detail.status] ?? detail.status}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">모델 버전</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {detail.model_version ?? "-"}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">시작 시각</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {detail.started_at
                  ? new Date(detail.started_at).toLocaleString("ko-KR")
                  : "-"}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">완료 시각</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {detail.completed_at
                  ? new Date(detail.completed_at).toLocaleString("ko-KR")
                  : "-"}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">처리 시간</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {detail.processing_time_ms != null
                  ? `${detail.processing_time_ms.toLocaleString()} ms`
                  : "-"}
              </dd>
            </div>
            {detail.error_message && (
              <div className="md:col-span-2">
                <dt className="text-sm font-medium text-gray-500">
                  오류 메시지
                </dt>
                <dd className="mt-1 text-sm text-red-600">
                  {detail.error_message}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* T2-2: 불변 증거 스냅숏 — 완료 시점 고정 기록 (레거시 분석은 "기록 없음") */}
      <div className="mb-8">
        <EvidenceCard evidence={detail.evidence ?? null} />
      </div>

      {/* 시료 정보 */}
      {detail.sample && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">시료 정보</h2>
          </div>
          <div className="p-6">
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">시료 ID</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {detail.sample.sample_id}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">시료명</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {detail.sample.name}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">시료 유형</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {detail.sample.sample_type}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">시료 상태</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {detail.sample.status}
                </dd>
              </div>
              {detail.sample.collection_location && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    채취 장소
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {detail.sample.collection_location}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      )}

      {/* 분석 결과 */}
      {detail.results && detail.results.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">분석 결과</h2>
            <p className="text-sm text-gray-500 mt-1">
              {/* A3: 관찰 단위 계수 — 후보 행은 독립 검출이 아니다.
                  candidate_count 는 이름 있는 행 기준 — 표의 행 수와 다르면
                  (이름 없는 레거시 행) 그 사실을 함께 밝힌다 */}
              {detail.observation_count != null
                ? `관찰(스캔) ${detail.observation_count}건 · 기록 후보 ${
                    detail.candidate_count ?? detail.results.length
                  }건${
                    detail.candidate_count != null &&
                    detail.results.length > detail.candidate_count
                      ? ` (표에는 이름 없는 행 포함 ${detail.results.length}행)`
                      : ""
                  }`
                : `기록 후보 ${detail.results.length}건`}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    화합물명
                  </th>
                  {SHOW_MATCH_PERCENTAGE && (
                    <>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        신뢰도
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        매칭 점수
                      </th>
                    </>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    머무름 시간
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    위험 지표
                  </th>
                  {/* T2-3: 감정인 판정 — 사람이 기록한 판단만 */}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    판정 (감정인)
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {detail.results.map((result: AnalysisResultItem) => (
                  <tr key={result.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {result.compound_name ?? "-"}
                    </td>
                    {SHOW_MATCH_PERCENTAGE && (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {result.confidence_score != null
                            ? `${(result.confidence_score * 100).toFixed(1)}%`
                            : "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {result.matching_score != null
                            ? `${(result.matching_score * 100).toFixed(1)}%`
                            : "-"}
                        </td>
                      </>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {result.retention_time != null
                        ? `${result.retention_time.toFixed(2)} min`
                        : "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <HazardIndicator
                        value={result.is_flammable}
                        label="인화성"
                      />
                      <HazardIndicator value={result.is_toxic} label="독성" />
                      <HazardIndicator
                        value={result.is_explosive}
                        label="폭발성"
                      />
                      {!result.is_flammable &&
                        !result.is_toxic &&
                        !result.is_explosive && (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {(() => {
                        const a = assessmentByResult.get(result.id);
                        if (!a)
                          return (
                            <span className="text-xs text-gray-400">
                              판정 입력 전
                            </span>
                          );
                        return (
                          <span
                            className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${assessmentBadgeClass(a.status)}`}
                            title={a.rationale}
                          >
                            {ASSESSMENT_STATUS_LABEL[a.status] ?? a.status}
                          </span>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
